// [S-286] Type-aware in-app viewer for a precedent's source document.
//   - PDF / image → rendered inline (browser-native via blob URL).
//   - DOCX        → converted to HTML with mammoth (lazy-loaded), sanitized
//                   with DOMPurify, shown read-only.
//   - text/*      → shown as preformatted text.
//   - anything else → download fallback (we never ship the bytes to an external
//                   viewer — contract documents stay in the browser).
// Token usage only — no hardcoded colors.
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Dialog } from "./Dialog";
import { Button } from "./Button";

const DOCX_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
]);

/** Blob → string via FileReader. Deliberately not `blob.text()`: see the text
 *  branch below — jsdom's Blob implements neither text() nor arrayBuffer(), and
 *  FileReader predates both in browsers too. */
function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // Narrow rather than String(): FileReader.result is `string | ArrayBuffer
    // | null`, so coercing it would render an ArrayBuffer as the literal
    // "[object ArrayBuffer]" (what no-base-to-string flags). readAsText below
    // always produces the string branch; the fallback is type-honesty, not a
    // reachable case.
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === "string" ? result : "");
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsText(blob);
  });
}

type Kind = "pdf" | "docx" | "text" | "image" | "other";

export function kindOf(type: string, filename: string): Kind {
  const t = (type || "").toLowerCase();
  const f = filename.toLowerCase();
  if (t.includes("pdf") || f.endsWith(".pdf")) return "pdf";
  if (DOCX_TYPES.has(t) || /\.docx?$/.test(f)) return "docx";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("text/") || t.includes("json") || t.includes("xml")) return "text";
  return "other";
}

function extOf(type: string): string {
  const t = (type || "").toLowerCase();
  if (t.includes("pdf")) return ".pdf";
  if (DOCX_TYPES.has(t)) return ".docx";
  if (t.startsWith("image/")) return `.${t.split("/")[1] ?? "img"}`;
  if (t.startsWith("text/")) return ".txt";
  return "";
}

export function SourceViewer({
  open,
  onOpenChange,
  blob,
  baseName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blob: Blob | null;
  baseName: string;
}): JSX.Element {
  const kind: Kind = blob ? kindOf(blob.type, baseName) : "other";

  // Object URL for the formats the browser renders natively.
  const objectUrl = useMemo(() => {
    if (blob !== null && (kind === "pdf" || kind === "image")) {
      return URL.createObjectURL(blob);
    }
    return null;
  }, [blob, kind]);
  useEffect(
    () => () => {
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl]
  );

  const [html, setHtml] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [convError, setConvError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setText(null);
    setConvError(null);
    if (blob === null) return;
    if (kind === "docx") {
      setConverting(true);
      blob
        .arrayBuffer()
        .then((arrayBuffer) =>
          import("mammoth").then((m) => m.default.convertToHtml({ arrayBuffer }))
        )
        .then((res) => {
          if (!cancelled) setHtml(DOMPurify.sanitize(res.value));
        })
        .catch((e: unknown) => {
          if (!cancelled)
            setConvError(
              e instanceof Error ? e.message : "Couldn't render this document."
            );
        })
        .finally(() => {
          if (!cancelled) setConverting(false);
        });
    } else if (kind === "text") {
      // [S-632 fix] Read via FileReader, not blob.text(). jsdom's Blob
      // implements NEITHER text() nor arrayBuffer() — this branch threw
      // "blob.text is not a function" and left the in-screen viewer test red on
      // main (the docx branch above has the same exposure; it just isn't
      // covered). FileReader is implemented in jsdom and predates both Blob
      // methods in every browser, so it works in the test and on old Safari.
      readBlobAsText(blob)
        .then((t) => {
          if (!cancelled) setText(t);
        })
        .catch(() => {
          if (!cancelled) setConvError("Couldn't read the file.");
        });
    }
    return () => {
      cancelled = true;
    };
  }, [blob, kind]);

  function download(): void {
    if (blob === null) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}${extOf(blob.type)}`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Source document"
      // Extend the base dialog styles (position/background/z-index) — passing
      // only "srcview__dialog" here would REPLACE ds-dialog__content and the
      // viewer would render unpositioned behind the overlay (invisible).
      contentClassName="ds-dialog__content srcview__dialog"
    >
      <div className="srcview" data-testid="source-viewer" data-kind={kind}>
        {blob === null ? null : kind === "pdf" && objectUrl !== null ? (
          <iframe className="srcview__pdf" src={objectUrl} title="Source document" />
        ) : kind === "image" && objectUrl !== null ? (
          <img className="srcview__img" src={objectUrl} alt="Source document" />
        ) : kind === "docx" ? (
          converting ? (
            <p className="srcview__state" role="status">
              Rendering document…
            </p>
          ) : convError !== null ? (
            <Fallback msg={convError} onDownload={download} />
          ) : html !== null ? (
            <div
              className="srcview__doc"
              // Sanitized with DOMPurify above.
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : null
        ) : kind === "text" ? (
          <pre className="srcview__text">{text}</pre>
        ) : (
          <Fallback
            msg={`Preview isn't available for this file type${
              blob.type ? ` (${blob.type})` : ""
            }.`}
            onDownload={download}
          />
        )}
      </div>
      <div className="srcview__footer">
        <Button variant="bare" onClick={download} data-testid="source-download">
          Download original ↓
        </Button>
      </div>
    </Dialog>
  );
}

function Fallback({
  msg,
  onDownload,
}: {
  msg: string;
  onDownload: () => void;
}): JSX.Element {
  return (
    <div className="srcview__fallback">
      <p>{msg}</p>
      <Button variant="primary" onClick={onDownload}>
        Download original ↓
      </Button>
    </div>
  );
}
